import { z } from 'zod';

/**
 * Admin BulkAuditLog query/response schemas — shared Zod (Phase 41 v1.9).
 *
 * Single source of truth for the `GET /admin/audit/bulk` surface that exposes
 * the BulkAuditLog table (shipped Phase 35 v1.8 — BULK-AUDIT-01). The admin
 * page consumes these to render the operations-audit list with filters,
 * cursor pagination, and per-row drill-down (requested vs processed IDs).
 *
 * Mirrors Prisma enum BulkAuditAcao (BULK_APROVAR | BULK_REJEITAR |
 * APROVAR_TODOS_PENDENTES). Framework-agnostic: no @prisma/client import —
 * enum literals are duplicated explicitly here so packages/shared remains
 * importable by the mobile bundle (which has no Prisma at runtime).
 *
 * Pagination: cursor token signed with `ep='audit_bulk'` via the shared
 * signCursor/verifyCursor helpers. Sort order is (at DESC, id DESC) so the
 * cursor payload encodes the boundary row's `at` timestamp + `id`.
 */

// ---------- Enums ----------

export const BulkAuditAcaoSchema = z.enum([
  'BULK_APROVAR',
  'BULK_REJEITAR',
  'APROVAR_TODOS_PENDENTES',
]);
export type BulkAuditAcao = z.infer<typeof BulkAuditAcaoSchema>;

// ---------- DTOs ----------

/**
 * Single BulkAuditLog row enriched with actor + evento minimal projection so
 * the admin UI can render "user X did Y on evento Z" without a second round-trip.
 *
 * `actor` is nullable to honour the LGPD-driven SetNull FK (Phase 35 schema):
 * when an actor user is deleted, the audit row survives with actorId=NULL.
 *
 * `evento` is non-nullable: Evento → BulkAuditLog FK is Cascade, so if the
 * evento is gone the audit row is gone too.
 */
export const BulkAuditLogDtoSchema = z.object({
  id: z.string(),
  acao: BulkAuditAcaoSchema,
  actorId: z.string().nullable(),
  actor: z
    .object({
      id: z.string(),
      nome: z.string(),
      email: z.string(),
    })
    .nullable(),
  eventoId: z.string(),
  evento: z.object({
    id: z.string(),
    nome: z.string(),
  }),
  candidaturaIdsRequested: z.array(z.string()),
  candidaturaIdsProcessed: z.array(z.string()),
  batchSize: z.number().int().min(0),
  motivo: z.string().nullable(),
  at: z.string(), // ISO 8601
});
export type BulkAuditLogDto = z.infer<typeof BulkAuditLogDtoSchema>;

// ---------- Page response ----------

export const BulkAuditPageResponseSchema = z.object({
  items: z.array(BulkAuditLogDtoSchema),
  nextCursor: z.string().nullable(),
});
export type BulkAuditPageResponse = z.infer<typeof BulkAuditPageResponseSchema>;

// ---------- Query ----------

/**
 * Query parameters for `GET /admin/audit/bulk`.
 *
 * All filters are optional and AND-combined server-side. Date range filter
 * uses `fromDate`/`toDate` as inclusive bounds against the `at` column. Both
 * dates accept ISO 8601 strings; the server coerces to Date.
 */
export const BulkAuditQuerySchema = z.object({
  acao: BulkAuditAcaoSchema.optional(),
  actorId: z.string().min(1).optional(),
  eventoId: z.string().min(1).optional(),
  fromDate: z
    .string()
    .datetime({ offset: true })
    .optional(),
  toDate: z
    .string()
    .datetime({ offset: true })
    .optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type BulkAuditQuery = z.infer<typeof BulkAuditQuerySchema>;
